"use client"

import { useMemo, useState, type ReactNode } from "react"
import { AlertTriangle } from "lucide-react"
import { refrigeratorLifespanAnswer, refrigeratorLifespanAnswerFaq } from "@/lib/tools/refrigerator-lifespan"
import {
  SectionRule,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"

// Scenario arithmetic, not a survival model or appliance diagnosis.
// Tier and usage settings are illustrative assumptions disclosed in the UI.
type Tone = "healthy" | "watch" | "replace"
type BrandTier = "budget" | "mid" | "premium"
type Usage = "light" | "normal" | "heavy"
type Noise = "quiet" | "mild" | "loud"

interface Warning {
  title: string
  description: string
}

interface Verdict {
  tone: Tone
  headline: string
  subline: string
  yearsLeft: number
  expected: number
  warnings: Warning[]
  currentKwh: number
  newKwh: number
  annualWasteDollars: number
}

const BASE_LIFESPAN: Record<BrandTier, number> = { budget: 10, mid: 14, premium: 18 }
const USAGE_MULT: Record<Usage, number> = { light: 1.15, normal: 1, heavy: 0.82 }
const BASE_KWH: Record<BrandTier, number> = { budget: 650, mid: 500, premium: 400 }

export function computeVerdict(
  age: number,
  brand: BrandTier,
  usage: Usage,
  repairs: number,
  noise: Noise,
  frost: "yes" | "no",
): Verdict {
  const expected = Math.round(BASE_LIFESPAN[brand] * USAGE_MULT[usage])
  const yearsLeft = Math.max(0, expected - age)

  const warnings: Warning[] = []
  if (repairs >= 2) warnings.push({
    title: "Repeated repairs reported",
    description: `${repairs} major repairs were entered. Compare a current written repair quote, warranty coverage and replacement cost; repair count alone does not determine the better choice.`,
  })
  if (noise === "loud") warnings.push({
    title: "Grinding noise reported",
    description: "Have the noise assessed against the manufacturer troubleshooting guidance. This input cannot identify a faulty component or predict a failure date.",
  })
  if (frost === "yes") warnings.push({
    title: "Frost buildup reported",
    description: "Check the manufacturer guidance for your model and arrange service if needed. Frost alone does not establish its cause or electricity consumption.",
  })
  const tone: Tone = warnings.length ? "watch" : "healthy"
  const headline = warnings.length ? "Review service options." : "Review your options."
  const subline = warnings.length
    ? "Your reported repair history or symptoms merit a closer look. Get a diagnosis and compare actual costs before deciding whether to repair or replace."
    : `You entered an age of ${age} years without repeated repairs, grinding noise or frost buildup. Age and price tier alone cannot tell us how long this refrigerator will last.`

  const ageDegrade = Math.min(0.4, age * 0.025)
  const currentKwh = Math.round(BASE_KWH[brand] * (1 + ageDegrade))
  const newKwh = 380
  const annualWasteDollars = Math.round((currentKwh - newKwh) * 0.16)

  return { tone, headline, subline, yearsLeft, expected, warnings, currentKwh, newKwh, annualWasteDollars }
}

// ---------- Tone color palette (matches app/globals.css CSS vars) ----------

const TONE_BORDER: Record<Tone, string> = {
  healthy: "#10a65c",
  watch: "#ff9951",
  replace: "#dc2626",
}
const TONE_BG: Record<Tone, string> = {
  healthy: "#ecfdf5",
  watch: "rgba(255,153,81,0.08)",
  replace: "#fef2f2",
}
const TONE_LABEL: Record<Tone, string> = {
  healthy: "Planning · No reported concerns",
  watch: "Planning · Service review",
  replace: "Planning · Compare options",
}
const TONE_CTA: Record<Tone, { label: string; href: string }> = {
  healthy: { label: "See maintenance tips →", href: "/tools/refrigerator-maintenance-schedule" },
  watch: { label: "Browse 2026 models →", href: "/collections/refrigerators" },
  replace: { label: "Shop replacements →", href: "/collections/refrigerators" },
}

// ---------- Small helpers ----------

function RadioButton({
  value,
  current,
  onClick,
  label,
  sub,
}: {
  value: string
  current: string
  onClick: (v: string) => void
  label: string
  sub?: string
}) {
  const active = current === value
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={[
        "px-3 py-2.5 text-left transition-all w-full rounded-lg border",
        active
          ? "bg-gray-900 text-white border-gray-900"
          : "bg-white text-gray-900 border-gray-200 hover:border-[#ff9951]",
      ].join(" ")}
    >
      <div className="font-bold text-[13px] leading-tight">{label}</div>
      {sub && (
        <div className={`text-[10px] mt-0.5 leading-tight ${active ? "text-white/60" : "text-gray-500"}`}>
          {sub}
        </div>
      )}
    </button>
  )
}

function SliderRow({
  label,
  value,
  suffix,
  min,
  max,
  onChange,
}: {
  label: string
  value: number
  suffix?: string
  min: number
  max: number
  onChange: (n: number) => void
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <label className="text-[12px] font-bold uppercase tracking-[0.1em]">{label}</label>
        <div className="font-black text-gray-900 leading-none" style={{ fontSize: 22, letterSpacing: "-0.03em" }}>
          {value}
          {suffix && <span className="text-[11px] font-bold text-gray-500 ml-1">{suffix}</span>}
        </div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(+e.target.value)}
        className="w-full"
        style={{ accentColor: "#ff9951" }}
      />
      <div className="flex justify-between text-[10px] text-gray-500 font-mono mt-0.5">
        <span>{min}</span>
        <span>
          {max}
          {suffix ? ` ${suffix}` : "+"}
        </span>
      </div>
    </div>
  )
}

function InputLabel({ children, hint }: { children: ReactNode; hint?: string }) {
  return (
    <div className="mb-2">
      <label className="block text-[12px] font-bold uppercase tracking-[0.1em] text-gray-900">
        {children}
      </label>
      {hint && (
        <span className="block text-[10px] text-gray-500 mt-0.5">{hint}</span>
      )}
    </div>
  )
}

// ---------- Page ----------

export function RefrigeratorLifespanCalculatorPageClient() {
  const [age, setAge] = useState(11)
  const [brand, setBrand] = useState<BrandTier>("mid")
  const [usage, setUsage] = useState<Usage>("normal")
  const [repairs, setRepairs] = useState(1)
  const [noise, setNoise] = useState<Noise>("mild")
  const [frost, setFrost] = useState<"yes" | "no">("no")

  const verdict = useMemo(
    () => computeVerdict(age, brand, usage, repairs, noise, frost),
    [age, brand, usage, repairs, noise, frost],
  )

  const toneBorder = TONE_BORDER[verdict.tone]
  const toneBg = TONE_BG[verdict.tone]
  const toneLabel = TONE_LABEL[verdict.tone]
  const toneCta = TONE_CTA[verdict.tone]

  return (
    <ToolPageShell
      toolName="Refrigerator Lifespan Calculator"
      heroEyebrow="Specialty"
      heroTitlePre="Should you replace"
      heroTitleItalic="your refrigerator?"
      heroSubtitle={refrigeratorLifespanAnswer()}
      heroMeta={["⏱ 45 sec", "Illustrative planning model"]}
      heroShare={<ToolShareBar title="Should you replace your refrigerator?" />}
      faqs={[
        // FRIDGE-902: the lifespan sentence under its question, first and open,
        // from the one function the hero and the FAQPage node also read.
        refrigeratorLifespanAnswerFaq(),
        {
          question: "Is 'replace' always about age?",
          answer:
            "No. Age alone does not establish a need to replace an appliance. This tool highlights the symptoms you report and shows an illustrative scenario; a repair diagnosis and actual costs are needed for a purchase decision.",
        },
        {
          question: "Why is your result different from a repair tech's?",
          answer:
            "A technician can inspect your appliance; this tool cannot. Use a written diagnosis, repair quote, warranty coverage and replacement price to compare your options.",
        },
        {
          question: "What about the environmental cost of a new fridge?",
          answer:
            "That comparison needs product-specific manufacturing information, actual electricity use, the local electricity mix and appropriate recycling. This calculator does not estimate environmental break-even.",
        },
        {
          question: "What if I just replaced the compressor last year?",
          answer:
            "Include it in the repair history, and check the repair warranty and current condition. A previous compressor repair alone does not establish that another repair or replacement is needed.",
        },
      ]}
      relatedTools={[
        {
          title: "Energy Cost Calculator",
          category: "Energy & Cost",
          description: "Calculate cost from annual kWh and your rate.",
          href: "/tools/energy-cost-calculator",
          time: "~30 sec",
        },
        {
          title: "Refrigerator Size Calculator",
          category: "Size & Capacity",
          description: "Size your replacement properly.",
          href: "/tools/refrigerator-size-calculator",
          time: "~1 min",
        },
        {
          title: "Find Your Fridge",
          category: "Specialty",
          description: "Explore product groups based on your answers.",
          href: "/tools/find-your-fridge",
          time: "~2 min",
        },
        {
          title: "Rebate Finder",
          category: "Energy & Cost",
          description: "Check current programs and eligibility.",
          href: "/tools/refrigerator-rebate-finder",
          time: "~30 sec",
        },
      ]}
      cta={{
        title: "Planning a replacement? We'll do the homework.",
        body: "Answer five questions to explore a starting product group, with prices where available. Verify dimensions, features and warranty terms on each product page.",
        buttonLabel: "Start fridge match",
        buttonHref: "/tools/find-your-fridge",
      }}
    >
      {/* ============ INPUTS + VERDICT ============ */}
      <section className="bg-white py-6 border-b border-gray-200">
        <p className="max-w-[1120px] mx-auto px-6 md:px-8 text-sm text-gray-600 leading-relaxed">
          Source: <a href="https://www.energystar.gov/products/refrigerators" className="text-[#0469f0] underline underline-offset-2">ENERGY STAR refrigerator savings guidance</a>.
          {' '}The Fridge.com calculator below uses separate tier and usage assumptions to frame a replacement decision; its estimates are not observed survival statistics or a diagnosis of your appliance.
        </p>
      </section>
      <section className="py-8 md:py-12 bg-gray-50 border-b border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 grid lg:grid-cols-[460px_1fr] gap-8 lg:gap-12">
          {/* Inputs panel — compact 2-col pairs */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 lg:sticky lg:top-24 lg:self-start">
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-5 text-[#ff9951]">
              Tell us about your fridge
            </div>

            {/* Every input gets its own full-width row — uses the vertical
                space under the verdict card and gives radios room to breathe */}
            <div className="space-y-6">
              <SliderRow
                label="Age"
                value={age}
                suffix="years"
                min={0}
                max={25}
                onChange={setAge}
              />

              <SliderRow
                label="Major repairs in last 5 years"
                value={repairs}
                min={0}
                max={5}
                onChange={setRepairs}
              />

              <div>
                <InputLabel hint="Illustrative settings, not measured brand reliability">Scenario tier</InputLabel>
                <div className="grid grid-cols-3 gap-2">
                  <RadioButton value="budget" current={brand} onClick={(v) => setBrand(v as BrandTier)} label="Budget" sub="10-year assumption" />
                  <RadioButton value="mid" current={brand} onClick={(v) => setBrand(v as BrandTier)} label="Mid-range" sub="14-year assumption" />
                  <RadioButton value="premium" current={brand} onClick={(v) => setBrand(v as BrandTier)} label="Premium" sub="18-year assumption" />
                </div>
              </div>

              <div>
                <InputLabel hint="Illustrative multiplier, not a measured wear rate">Usage scenario</InputLabel>
                <div className="grid grid-cols-3 gap-2">
                  <RadioButton value="light" current={usage} onClick={(v) => setUsage(v as Usage)} label="Light" sub="1–2 people" />
                  <RadioButton value="normal" current={usage} onClick={(v) => setUsage(v as Usage)} label="Normal" sub="3–4 people" />
                  <RadioButton value="heavy" current={usage} onClick={(v) => setUsage(v as Usage)} label="Heavy" sub="5+ or rental" />
                </div>
              </div>

              <div>
                <InputLabel>Compressor noise</InputLabel>
                <div className="grid grid-cols-3 gap-2">
                  <RadioButton value="quiet" current={noise} onClick={(v) => setNoise(v as Noise)} label="Quiet" sub="Background hum" />
                  <RadioButton value="mild" current={noise} onClick={(v) => setNoise(v as Noise)} label="Mild" sub="Occasional cycle" />
                  <RadioButton value="loud" current={noise} onClick={(v) => setNoise(v as Noise)} label="Grinding" sub="Hear across room" />
                </div>
              </div>

              <div>
                <InputLabel>Frost buildup in freezer?</InputLabel>
                <div className="grid grid-cols-2 gap-2">
                  <RadioButton value="no" current={frost} onClick={(v) => setFrost(v as "yes" | "no")} label="No" sub="Clean walls, clear shelves" />
                  <RadioButton value="yes" current={frost} onClick={(v) => setFrost(v as "yes" | "no")} label="Yes" sub="Ice on walls / door seal" />
                </div>
              </div>
            </div>
          </div>

          {/* Verdict — the star */}
          <div>
            {/* Verdict card */}
            <div
              className="rounded-2xl p-6 sm:p-8 md:p-10 mb-6 md:mb-8"
              style={{ background: toneBg, border: `1px solid ${toneBorder}30` }}
            >
              <div
                className="text-[11px] font-bold uppercase tracking-[0.18em] mb-6 flex items-center gap-2"
                style={{ color: toneBorder }}
              >
                <span className="w-2 h-2 rounded-full inline-block" style={{ background: toneBorder }} />
                {toneLabel}
              </div>
              <div
                className="text-gray-900 font-black"
                style={{
                  fontSize: "clamp(44px, 7vw, 80px)",
                  letterSpacing: "-0.05em",
                  lineHeight: 0.95,
                  textWrap: "balance" as never,
                }}
              >
                {verdict.headline}
              </div>
              <p
                className="mt-5 md:mt-6 text-gray-700"
                style={{ fontSize: 17, lineHeight: 1.45, textWrap: "pretty" as never }}
              >
                {verdict.subline}
              </p>
            </div>

            {/* Metrics strip */}
            <div className="grid grid-cols-3 border-y border-gray-300 divide-x divide-gray-300 mb-6 md:mb-8">
              <div className="py-5 pr-5">
                <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">
                  Scenario horizon
                </div>
                <div className="font-black text-gray-900" style={{ fontSize: 30, letterSpacing: "-0.03em" }}>
                  {verdict.expected}
                  <span className="text-[13px] font-bold text-gray-500 ml-1">yrs</span>
                </div>
              </div>
              <div className="py-5 px-5">
                <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">
                  Horizon minus age
                </div>
                <div
                  className="font-black"
                  style={{ fontSize: 30, letterSpacing: "-0.03em", color: toneBorder }}
                >
                  {verdict.yearsLeft}
                  <span className="text-[13px] font-bold text-gray-500 ml-1">yrs</span>
                </div>
              </div>
              <div className="py-5 pl-5">
                <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">
                  Example cost difference
                </div>
                <div
                  className="font-black"
                  style={{
                    fontSize: 30,
                    letterSpacing: "-0.03em",
                    color: verdict.annualWasteDollars > 40 ? toneBorder : "#111827",
                  }}
                >
                  {`$${verdict.annualWasteDollars}`}
                </div>
              </div>
            </div>

            {/* Evidence list */}
            {verdict.warnings.length > 0 && (
              <div className="mb-8">
                <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-4 text-gray-900">
                  Inputs to discuss with a technician
                </div>
                <ul className="space-y-2">
                  {verdict.warnings.map((w) => (
                    <li
                      key={w.title}
                      className="flex items-start gap-3 p-4 rounded-lg bg-white border border-gray-200"
                    >
                      <div
                        className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-white"
                        style={{ background: toneBorder }}
                      >
                        <AlertTriangle className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="font-extrabold text-[14px]">{w.title}</div>
                        <p className="text-[13px] text-gray-600 leading-[1.5] mt-0.5">{w.description}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Action row — single primary CTA, no fake "Email me" button */}
            <a
              href={toneCta.href}
              className="block h-14 rounded-lg text-[15px] font-extrabold tracking-tight text-white inline-flex items-center justify-center w-full"
              style={{
                background: verdict.tone === "replace" ? "#ff9951" : "#111827",
                boxShadow:
                  verdict.tone === "replace" ? "0 8px 22px rgba(255,153,81,0.35)" : undefined,
              }}
            >
              {toneCta.label}
            </a>
          </div>
        </div>
      </section>

      {/* ============ PRODUCT CAROUSEL — reliable replacements (real DB) ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="If it's time"
          title="Explore refrigerator models"
          description="Compare model specifications, offers and manufacturer warranty terms."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="py-8 md:py-12 bg-gray-50 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="How we decide" title="The methodology" />
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                n: "01",
                t: "Scenario assumptions",
                d: "The three scenario horizons are 10, 14 and 18 years, multiplied by 1.15, 1 or 0.82 for the usage setting and rounded. These are illustrative Fridge.com assumptions, not AHAM averages, brand reliability measurements or predicted failure dates.",
              },
              {
                n: "02",
                t: "Example electricity arithmetic",
                d: "Example annual use starts at 650, 500 or 400 kWh by scenario tier, increases 2.5% per entered year up to 40%, and is compared with 380 kWh at $0.16/kWh. These assumed inputs do not measure your refrigerator, establish waste or calculate replacement payback.",
              },
              {
                n: "03",
                t: "Compare actual options",
                d: "Repeated repairs, grinding noise or frost prompt a service review. No age, symptom or repair count on its own calls for replacement. Compare the diagnosis, written repair quote, remaining warranty, replacement price and measured electricity use.",
              },
            ].map((m) => (
              <div key={m.n}>
                <div className="font-mono text-[11px] font-bold tracking-[0.15em] mb-3 text-[#ff9951]">
                  {m.n}
                </div>
                <h3 className="text-[22px] font-extrabold tracking-[-0.02em] mb-3 text-gray-900">
                  {m.t}
                </h3>
                <p className="text-[14px] text-gray-600 leading-[1.6]">{m.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL — "you might also like" (different collection) ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Side-by-side replacements"
          description="Compare side-by-side dimensions and features for your kitchen."
          collectionType="side-by-side"
          fallbackCollectionType="top-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
